import { XMLParser } from 'fast-xml-parser';
import { DEFAULTS } from '../grid/defaults.js';
import {
  ReportIR,
  CellIR,
  RowIR,
  ColumnIR,
  DatasetIR,
  DatasourceIR,
  ParameterIR,
  FormElementIR,
  FillIR,
  CellControlIR,
  CellFacadeIR,
  AggregateType,
} from '../ir/types.js';

/**
 * BaskReport XML → ReportIR（JSON 中间层）。
 *
 * 设计目标：parse 与 serialize 互为语义逆操作 —— 对 `parse(xml)` 的结果再 `serializeToXml`
 * 得到的 XML 与原 XML **语义等价**（单元格内容/父格/合并/数据集一致），但不保证字节级逐属性一致
 * （空白格剪枝、DEFAULTS 剪枝、工具栏/设置节点不承载）。
 *
 * 范围：
 *  - 解析 `<report>` 根；`<dashboard>` 根（仪表盘/图表）不在 IR 承载范围内，返回 null。
 *  - 解析单元格（含 control/facade）、行/列、数据集/数据源、参数、参数表单、填报。
 *  - 忽略 `<setting>/<preview>/<tool>` 等工具栏配置（用户确认可丢失）。
 */

const AGGREGATE_MAP: Record<string, AggregateType> = {
  group: 'group',
  sum: 'sum',
  count: 'count',
  average: 'avg',
  avg: 'avg',
  max: 'max',
  min: 'min',
  select: 'select',
  none: 'none',
};

/** editor-type 归一映射（与 serialize.ts 保持同义，保证 XML↔IR 往返一致）。 */
const EDITOR_TYPE_MAP: Record<string, string> = {
  text: 'text',
  textarea: 'text',
  input: 'text',
  select: 'select',
  dropdown: 'select',
  combo: 'select',
  combobox: 'select',
  date: 'date',
  datepicker: 'date',
  calendar: 'date',
  button: 'button',
};

function normalizeEditorType(t?: string): string {
  if (!t) return 'text';
  const key = t.toLowerCase();
  return EDITOR_TYPE_MAP[key] ?? t;
}

/** 把 A1 风格坐标（如 B3）转为 {row, col}（1 基）。 */
function parseCoord(name: string | undefined): { row: number; col: number } | null {
  if (!name) return null;
  const m = /^([A-Z]+)(\d+)$/.exec(name);
  if (!m) return null;
  const letters = m[1]!;
  const digits = m[2]!;
  let col = 0;
  for (const ch of letters) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { col, row: parseInt(digits, 10) };
}

function num(v: string | undefined): number | undefined {
  if (v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function bool(v: string | undefined): boolean | undefined {
  if (v === undefined || v === '') return undefined;
  return v === 'true' || v === '1';
}

function str(v: string | undefined): string | undefined {
  return v === undefined || v === '' ? undefined : v;
}

/** 数组化：XMLParser 单节点为对象，多节点为数组。 */
function asArray<T>(v: T | T[] | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/** 读取元素属性（去掉 @_ 前缀）。 */
function attrs(el: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (!el || typeof el !== 'object') return out;
  for (const [k, v] of Object.entries(el)) {
    if (k.startsWith('@_')) out[k.slice(2)] = String(v);
  }
  return out;
}

/** 读取第一个名为 tag 的子元素（对象）。 */
function child(el: any, tag: string): any | undefined {
  if (!el) return undefined;
  const c = el[tag];
  if (c === undefined) return undefined;
  return Array.isArray(c) ? c[0] : c;
}

/** 读取所有名为 tag 的子元素。 */
function children(el: any, tag: string): any[] {
  if (!el) return [];
  return asArray(el[tag]);
}

/**
 * 解析单元格内容。
 * 支持：<text-content>、<dataset-content>、<expression-content>、<constant-content>。
 * 对齐 IR 的 CellContent 形状：{kind, value?|dataset,field,aggregate?}。
 */
function parseContent(cellEl: any): CellIR['content'] {
  const dataset = child(cellEl, 'dataset-content');
  if (dataset) {
    const a = attrs(dataset);
    const ir: any = { kind: 'dataset', dataset: a['dataset'] ?? '', field: a['field'] ?? '' };
    const agg = a['aggregate-type'];
    if (agg && agg !== 'select') ir.aggregate = (AGGREGATE_MAP[agg] ?? agg) as AggregateType;
    return ir;
  }
  const text = child(cellEl, 'text-content');
  if (text !== undefined) {
    const raw = typeof text === 'string' ? text : (text['#'] !== undefined ? String(text['#']) : '');
    return { kind: 'text', value: raw };
  }
  const expr = child(cellEl, 'expression-content');
  if (expr !== undefined) {
    const raw = typeof expr === 'string' ? expr : (expr['#'] !== undefined ? String(expr['#']) : '');
    return { kind: 'expression', value: raw };
  }
  const constant = child(cellEl, 'constant-content');
  if (constant !== undefined) {
    const raw = typeof constant === 'string' ? constant : (constant['#'] !== undefined ? String(constant['#']) : '');
    // 常量在 IR 中无独立 kind，归并为 text。
    return { kind: 'text', value: raw };
  }
  return undefined;
}

/** 解析单元格样式，与 DEFAULTS 比对剪枝。仅输出 StyleIR 承载的字段。 */
function parseStyle(a: Record<string, string>, cellEl?: any): CellIR['style'] {
  const style: NonNullable<CellIR['style']> = {};
  const align = a['h-align'] ?? a['align'];
  const valign = a['v-align'] ?? a['valign'];
  const forecolor = str(a['forecolor'] ?? a['fore-color']);
  const bgcolor = str(a['bgcolor'] ?? a['back-color']);
  const fontFamily = str(a['font-family']);
  const bold = bool(a['bold']);
  const italic = bool(a['italic']);
  const underline = bool(a['underline']);
  const lineThrough = bool(a['line-through']);
  const textDirection = str(a['text-direction']);
  const letterSpacing = num(a['letter-spacing']);
  // 格式化信息来自 <format> 子元素（type 属性 + CDATA 文本），而非 cell 属性。
  const fmtEl = cellEl ? child(cellEl, 'format') : undefined;
  const fa = fmtEl ? attrs(fmtEl) : {};
  const fmt = str(fmtEl ? (typeof fmtEl === 'string' ? fmtEl : (fmtEl['#'] !== undefined ? String(fmtEl['#']) : '')) : undefined);
  const formatType = str(fa['type']);
  const visible = bool(a['visible']);

  if (align && align !== DEFAULTS.align) style.align = align as any;
  if (valign && valign !== DEFAULTS.valign) style.valign = valign as any;
  if (forecolor && forecolor !== DEFAULTS.forecolor) style.forecolor = forecolor;
  if (bgcolor && bgcolor !== DEFAULTS.bgColor) style.bgcolor = bgcolor;
  if (fontFamily && fontFamily !== DEFAULTS.fontFamily) style.fontFamily = fontFamily;
  if (bold === true) style.bold = true;
  if (italic === true) style.italic = true;
  if (underline === true) style.underline = true;
  if (lineThrough === true) style.lineThrough = true;
  if (textDirection) style.textDirection = textDirection;
  if (letterSpacing !== undefined) style.letterSpacing = letterSpacing;
  if (fmt) style.format = fmt;
  if (formatType) style.formatType = formatType as any;
  if (visible === false) style.visible = false;

  return Object.keys(style).length ? style : undefined;
}

function parseControl(controlEl: any): CellControlIR | undefined {
  if (!controlEl) return undefined;
  const a = attrs(controlEl);
  const verifyEl = child(controlEl, 'verify');
  const bindingEl = child(controlEl, 'binding');
  const ir: CellControlIR = { type: a['type'] ?? 'text' };
  const visible = bool(a['visible']);
  const enableEmpty = bool(a['enable-empty']);
  const enableEdit = bool(a['enable-edit']);
  const enableCustom = bool(a['enable-custom']);
  const datePattern = str(a['date-pattern']);
  if (visible !== undefined) ir.visible = visible;
  if (enableEmpty !== undefined) ir.enableEmpty = enableEmpty;
  if (enableEdit !== undefined) ir.enableEdit = enableEdit;
  if (enableCustom !== undefined) ir.enableCustom = enableCustom;
  if (datePattern) ir.datePattern = datePattern;

  if (verifyEl) {
    const va = attrs(verifyEl);
    ir.verify = {
      type: va['type'] ?? 'length',
      minLength: num(va['min-length']),
      maxLength: num(va['max-length']),
      min: num(va['min']),
      max: num(va['max']),
    };
  }
  if (bindingEl) {
    const ba = attrs(bindingEl);
    const data = children(bindingEl, 'data').map((d) => {
      const da = attrs(d);
      return { label: da['label'] ?? '', value: da['value'] ?? '' };
    });
    ir.binding = { type: ba['type'] ?? 'custom' };
    if (data.length) ir.binding.customData = data;
  }
  return ir;
}

function parseFacade(facadeEl: any): CellFacadeIR | undefined {
  if (!facadeEl) return undefined;
  const a = attrs(facadeEl);
  const type = a['type'] ?? 'none';
  const mappingEl = child(facadeEl, 'mapping');
  const data = mappingEl
    ? children(mappingEl, 'data').map((d) => {
        const da = attrs(d);
        return { label: da['label'] ?? '', value: da['value'] ?? '' };
      })
    : [];
  const ir: CellFacadeIR = {};
  if (type !== 'none') ir.mappingType = type;
  const showOriginal = bool(a['show-original-value-on-mapping-fail']);
  if (showOriginal === true) ir.showOriginalValueOnMappingFail = true;
  if (data.length) ir.customMappingData = data;
  // type="none" 且无映射数据时返回 undefined（serialize 会自动补 none）
  if (ir.mappingType === undefined && ir.customMappingData === undefined && showOriginal === undefined) {
    return undefined;
  }
  return ir;
}

function parseCell(cellEl: any): CellIR {
  const a = attrs(cellEl);
  const coord = parseCoord(a['name']);
  const cell: CellIR = {
    row: coord?.row ?? num(a['row']) ?? 0,
    col: coord?.col ?? num(a['col']) ?? 0,
  };
  if (a['name']) cell.name = a['name'];

  const content = parseContent(cellEl);
  if (content) cell.content = content;

  const left = a['left'];
  const top = a['top'];
  if (left && left !== 'default') cell.left = left;
  if (top && top !== 'default') cell.top = top;

  const expand = a['expand'];
  if (expand && expand !== 'none') cell.expand = expand as any;

  const rowspan = num(a['rowspan']);
  const colspan = num(a['colspan']);
  if (rowspan !== undefined && rowspan !== 1) cell.rowspan = rowspan;
  if (colspan !== undefined && colspan !== 1) cell.colspan = colspan;

  const visible = bool(a['visible']);
  if (visible === false) cell.visible = false;

  const forceBefore = bool(a['force-paging-before']);
  const force = bool(a['force-paging']);
  if (forceBefore === true) cell.forcePagingBefore = true;
  if (force === true) cell.forcePaging = true;

  const agg = a['aggregate-type'];
  if (agg && agg !== 'none') {
    const mapped = AGGREGATE_MAP[agg];
    if (mapped) cell.aggregateType = mapped;
    const on = a['aggregate-on'];
    if (on === 'left' || on === 'top' || on === 'both') cell.aggregateOn = on;
  }

  const style = parseStyle(a, cellEl);
  if (style) cell.style = style;

  const control = parseControl(child(cellEl, 'control'));
  if (control) cell.control = control;

  const facade = parseFacade(child(cellEl, 'facade'));
  if (facade) cell.facade = facade;

  return cell;
}

function parseRow(rowEl: any): RowIR {
  const a = attrs(rowEl);
  const r: RowIR = { num: num(a['num']) ?? 0 };
  const h = num(a['height']);
  const visible = bool(a['visible']);
  const auto = bool(a['auto-height']);
  if (h !== undefined) r.height = h;
  if (visible === false) r.visible = false;
  if (auto === true) r.autoHeight = true;
  return r;
}

function parseCol(colEl: any): ColumnIR {
  const a = attrs(colEl);
  const c: ColumnIR = { num: num(a['num']) ?? 0 };
  const w = num(a['width']);
  const visible = bool(a['visible']);
  const auto = bool(a['auto-width']);
  if (w !== undefined) c.width = w;
  if (visible === false) c.visible = false;
  if (auto === true) c.autoWidth = true;
  return c;
}

function parseDataset(dsEl: any, datasources: DatasourceIR[]): DatasetIR {
  const a = attrs(dsEl);
  const rawType = (a['type'] ?? 'sql').toLowerCase();
  // 类型识别：buildin → buildin；api/adapt/virtual → 统一归为 api（服务端已登记数据集）；
  // 其余归 sql。
  const dsType: 'sql' | 'buildin' | 'api' =
    rawType === 'builtin' || rawType === 'buildin'
      ? 'buildin'
      : rawType === 'api' || rawType === 'adapt' || rawType === 'virtual'
        ? 'api'
        : 'sql';
  const ds: DatasetIR = {
    name: a['name'] ?? '',
    type: dsType,
    datasource: '',
    fields: [],
  };

  // 数据源引用：优先 <datasource> 子元素，其次 dataset 上的 datasource 属性
  const dsAttr = child(dsEl, 'datasource');
  if (dsAttr) {
    const da = attrs(dsAttr);
    ds.datasource = da['name'] ?? da['ref'] ?? '';
    if (da['type']) {
      const dt = (da['type'] as string).toLowerCase();
      ds.type = dt === 'buildin' ? 'buildin' : dt === 'api' || dt === 'adapt' || dt === 'virtual' ? 'api' : 'sql';
    }
    // 内联数据源定义（buildin-datasource）
    if (da['name'] && da['type']) {
      datasources.push({
        name: da['name'],
        type: da['type'] === 'buildin' ? 'buildin' : (da['type'] as any),
      });
    }
  } else if (a['datasource']) {
    ds.datasource = a['datasource'];
  } else if (a['datasource-id']) {
    // 官方格式：dataset 上用 datasource-id 引用服务端已配置数据源（serialize 端同样输出该属性）
    ds.datasource = a['datasource-id'];
  }
  if (ds.datasource) ds.datasource = ds.datasource;

  // <sql> 可能是字符串（CDATA 直接落为文本）或 { '#': '...' }
  const sqlEl = child(dsEl, 'sql');
  if (sqlEl !== undefined) {
    const sqlText = typeof sqlEl === 'string' ? sqlEl : (sqlEl['#'] !== undefined ? String(sqlEl['#']) : '');
    if (sqlText.trim()) ds.sql = sqlText.trim();
  }

  // 内联数据（builtin 数据集，<data><![CDATA[...]]></data>）
  const dataEl = child(dsEl, 'data');
  if (dataEl !== undefined) {
    const raw = typeof dataEl === 'string' ? dataEl : (dataEl['#'] !== undefined ? String(dataEl['#']) : '');
    if (raw.trim()) ds.data = raw.trim();
  }

  // 字段：<field> 可能直接挂在 <dataset> 下（官方格式），也可能包在 <fields> 内
  const fieldsParent = child(dsEl, 'fields') ?? dsEl;
  const fieldEls = children(fieldsParent, 'field');
  if (fieldEls.length) {
    ds.fields = fieldEls.map((f) => {
      const fa = attrs(f);
      return {
        name: fa['name'] ?? '',
        type: (fa['type'] ?? 'string') as any,
        label: fa['label'],
      };
    });
  }
  return ds;
}

function parseDatasource(dEl: any): DatasourceIR {
  const a = attrs(dEl);
  const isBuildin = a['type'] === 'buildin';
  const ds: DatasourceIR = {
    name: a['name'] ?? a['id'] ?? '',
    type: a['type'] ?? '',
    // buildin=内置数据源；其余（mysql/oracle 等）视为引用服务端已配置数据源
    kind: isBuildin ? 'buildin' : 'server',
  };
  const id = str(a['id']);
  if (id) ds.id = id;
  // buildin 分支补 buildinDatasource；server 分支按 id 引用，不重复定义连接细节
  if (isBuildin) {
    const buildin = str(a['buildin-datasource'] ?? a['buildinDatasource']);
    if (buildin) ds.buildinDatasource = buildin;
  } else if (id) {
    // 仅当存在服务端 id 时，保留 datasourceType（如 jdbc/virtual/api/adapt）以补全引用信息
    const dsType = str(a['datasource-type'] ?? a['datasourceType']);
    if (dsType) ds.datasourceType = dsType as any;
  }
  return ds;
}

function parseParameter(pEl: any): ParameterIR {
  const a = attrs(pEl);
  const rawType = (a['datatype'] ?? 'string').toLowerCase();
  const typeMap: Record<string, 'string' | 'number' | 'date' | 'boolean'> = {
    string: 'string',
    str: 'string',
    number: 'number',
    numeric: 'number',
    int: 'number',
    date: 'date',
    datetime: 'date',
    boolean: 'boolean',
    bool: 'boolean',
  };
  const p: ParameterIR = {
    name: a['name'] ?? '',
    type: typeMap[rawType] ?? 'string',
  };
  const dv = str(a['default-value']) ?? '';
  p.defaultValue = dv;
  return p;
}

function parseFormElement(feEl: any): FormElementIR {
  const a = attrs(feEl);
  const fe: FormElementIR = {
    name: a['name'] ?? '',
    formType: a['form-type'] ?? 'param',
  };
  const paramType = str(a['param-type']);
  if (paramType) fe.paramType = paramType;
  const label = str(a['label']);
  if (label) fe.label = label;
  const editor = str(a['editor-type']);
  if (editor) fe.editorType = normalizeEditorType(editor);
  const width = num(a['width']);
  if (width !== undefined) fe.width = width;
  const required = bool(a['required']);
  if (required !== undefined) fe.required = required;
  const left = num(a['left']);
  if (left !== undefined) fe.left = left;
  const top = num(a['top']);
  if (top !== undefined) fe.top = top;
  const soc = bool(a['submit-on-change']);
  if (soc !== undefined) fe.submitOnChange = soc;
  const aps = bool(a['apply-prefix-suffix-when-empty']);
  if (aps !== undefined) fe.applyPrefixSuffixWhenEmpty = aps;

  const bindingEl = child(feEl, 'binding');
  if (bindingEl) {
    const ba = attrs(bindingEl);
    const data = children(bindingEl, 'data').map((d) => {
      const da = attrs(d);
      return { label: da['label'] ?? '', value: da['value'] ?? '' };
    });
    fe.binding = { type: ba['type'] ?? 'custom' };
    if (data.length) fe.binding.customData = data;
  }
  return fe;
}

function parseFill(fillEl: any): FillIR {
  const submitEl = child(fillEl, 'submit');
  const sa = attrs(submitEl);
  const fields = children(submitEl, 'field').map((f) => {
    const fa = attrs(f);
    return {
      name: fa['name'] ?? '',
      type: fa['type'] ?? 'string',
      primaryKey: bool(fa['primary-key']) === true,
      autoIncrement: bool(fa['auto-increment']) === true,
      valueType: fa['value-type'] ?? 'cell',
      value: fa['value'] ?? '',
    };
  });
  return {
    submit: {
      name: sa['name'] ?? '',
      type: sa['type'] ?? 'sql',
      submitType: str(sa['submit-type']),
      datasource: sa['datasource'] ?? '',
      table: sa['table'] ?? '',
      fields,
    },
  };
}

/**
 * 将 BaskReport XML 字符串解析为 ReportIR。
 * @returns 解析后的 ReportIR；若根为 `<dashboard>`（仪表盘/图表）则返回 null。
 */
export function parseReport(xml: string): ReportIR | null {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    textNodeName: '#',
    removeNSPrefix: false,
    parseAttributeValue: false,
    parseTagValue: false,
    trimValues: true,
    isArray: (name) =>
      ['cell', 'row', 'col', 'dataset', 'buildin-datasource', 'datasource', 'parameter',
       'form-element', 'field', 'data', 'value'].includes(name),
  });
  const parsed = parser.parse(xml) as any;

  // 根节点可能是 report 或 baskreport（带 version 属性的真实格式）
  const root = parsed.report ?? parsed.baskreport;
  if (!root) return null;

  // 仪表盘/图表不在 IR 承载范围
  if (parsed.dashboard || root.dashboard) return null;

  const report: ReportIR = { cells: [], rowCount: 1, columnCount: 1 };

  // 单元格
  for (const cellEl of children(root, 'cell')) {
    report.cells.push(parseCell(cellEl));
  }

  // 行 / 列（仅记录 XML 中显式声明的，缺省由 serialize 补全）
  const rows: RowIR[] = [];
  for (const r of children(root, 'row')) rows.push(parseRow(r));
  if (rows.length) report.rows = rows;

  const cols: ColumnIR[] = [];
  for (const c of children(root, 'col')) cols.push(parseCol(c));
  if (cols.length) report.columns = cols;

  // 数据集 / 数据源
  const datasources: DatasourceIR[] = [];
  const datasets: DatasetIR[] = [];
  for (const dsEl of children(root, 'dataset')) {
    datasets.push(parseDataset(dsEl, datasources));
  }
  for (const dEl of children(root, 'buildin-datasource')) {
    datasources.push(parseDatasource(dEl));
  }
  // 内联数据源已在 parseDataset 中收集；去重
  const seen = new Set(datasources.map((d) => d.name));
  for (const dEl of children(root, 'datasource')) {
    const d = parseDatasource(dEl);
    if (!seen.has(d.name)) {
      datasources.push(d);
      seen.add(d.name);
    }
  }
  if (datasets.length) report.datasets = datasets;
  if (datasources.length) report.datasources = datasources;

  // 参数
  const parameters: ParameterIR[] = [];
  for (const pEl of children(root, 'parameter')) parameters.push(parseParameter(pEl));
  if (parameters.length) report.parameters = parameters;

  // 参数表单元素
  const formElements: FormElementIR[] = [];
  for (const feEl of children(root, 'form-element')) formElements.push(parseFormElement(feEl));
  if (formElements.length) report.parameterForm = formElements;

  // 填报
  const fillEl = child(root, 'fill');
  if (fillEl) report.fill = parseFill(fillEl);

  // 纸张 / 标题（尽量捕获，缺省回退）
  const paperEl = child(root, 'page-setting') ?? child(root, 'paper');
  if (paperEl) {
    const pa = attrs(paperEl);
    const paper: NonNullable<ReportIR['paper']> = {};
    const p = str(pa['paper']);
    const PAPER_ENUM = ['A4', 'A5', 'A6', 'B5', 'custom'];
    if (p) paper.paper = (PAPER_ENUM.includes(p) ? p : 'custom') as any;
    const pw = num(pa['paper-width']);
    if (pw !== undefined) paper.paperWidth = pw;
    const ph = num(pa['paper-height']);
    if (ph !== undefined) paper.paperHeight = ph;
    const mt = num(pa['top-margin']);
    if (mt !== undefined) paper.marginTop = mt;
    const mb = num(pa['bottom-margin']);
    if (mb !== undefined) paper.marginBottom = mb;
    const ml = num(pa['left-margin']);
    if (ml !== undefined) paper.marginLeft = ml;
    const mr = num(pa['right-margin']);
    if (mr !== undefined) paper.marginRight = mr;
    const ori = str(pa['orientation']);
    if (ori === 'portrait' || ori === 'landscape') paper.orientation = ori;
    if (Object.keys(paper).length) report.paper = paper;
  }

  const titleEl = child(root, 'title');
  if (titleEl && typeof titleEl === 'object') {
    const raw = str(titleEl['#'] ?? attrs(titleEl)['name']);
    if (raw != null) {
      // 剥离序列化时加的首尾双引号，避免 round-trip 二次序列化时双引号叠加，
      // 同时保留 fileName() 等函数表达式原样。
      report.title =
        raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"')
          ? raw.slice(1, -1)
          : raw;
    }
  }

  // rowCount / columnCount：取单元格与显式 rows/cols 的最大值，供 schema 校验。
  let maxRow = rows.reduce((m, r) => Math.max(m, r.num), 0);
  let maxCol = cols.reduce((m, c) => Math.max(m, c.num), 0);
  for (const c of report.cells) {
    maxRow = Math.max(maxRow, c.row + (c.rowspan ?? 1) - 1);
    maxCol = Math.max(maxCol, c.col + (c.colspan ?? 1) - 1);
  }
  report.rowCount = Math.max(maxRow, 1);
  report.columnCount = Math.max(maxCol, 1);

  return report;
}
